// src/Screens/seller/Dashboard/DashboardView.jsx
import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import SellerMasterLayout from "../../../layouts/seller/SellerMasterLayout";
import "../../../assets/css/seller/screens/SellerDashboardManagement.css";
import {
  FiLayers,
  FiClipboard,
  FiBox,
  FiAlertCircle,
  FiX,
  FiArrowRight,
} from "react-icons/fi";
import AppContext from "../../../contexts/AppContext";
import { useNavigate } from "react-router-dom";

import StackedBarChart from "../../../components/general/barchart/BarChart";

const DashboardView = () => {
  const { baseUrl, sellerAuthToken } = useContext(AppContext);
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [shopStatus, setShopStatus] = useState(null);

  const getDashboardData = () => {
    setLoading(true);
    const seller = JSON.parse(localStorage.getItem("loggedInSeller"));

    axios
      .get(`${baseUrl}/seller/dashboard/stats`, {
        params: { seller_id: seller?.id },
        headers: {
          Authorization: `Bearer ${sellerAuthToken}`,
        },
      })
      .then((res) => {
        setDashboardData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log("DASHBOARD API ERROR:", err.response?.data || err.message);
        setLoading(false);
      });
  };

const checkShopStatus = () => {
  const seller = JSON.parse(localStorage.getItem("loggedInSeller"));
  if (!seller?.id) return;

  const fd = new FormData();
  fd.append("seller_id", seller.id);

  axios
    .post(`${baseUrl}/seller/store/getmystore`, fd, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${sellerAuthToken}`,
      },
    })
    .then((res) => {
      const store = res.data.store;

      if (!store?.store_name) {
        setShopStatus("no-shop");
        setShowModal(true);
      } else {
        setShopStatus(store.status);
        if (store.status === "Pending" || store.status === "Rejected") {
          setShowModal(true);
        }
      }
    })
    .catch((err) => {
      console.log("API ERROR:", err.response?.data || err.message);
    });
};

useEffect(() => {
  if (!sellerAuthToken) {
    navigate("/seller/login"); 
    return;
  }
  getDashboardData();
  checkShopStatus();
}, [sellerAuthToken]);



  const handleCreateStore = () => {
    setShowModal(false);
    navigate("/seller/editstore");
  };

  const getModalContent = () => {
    if (shopStatus === "no-shop") {
      return {
        emoji: "🏪",
        title: "Welcome to CraftHive!",
        text: "Your seller account is all set. To start listing products and reaching customers, please set up your shop profile first.",
        confirmLabel: "Create My Shop",
        onConfirm: handleCreateStore,
        showCancel: true,
        closeable: true,
      };
    }
    if (shopStatus === "Pending") {
      return {
        emoji: "⏳",
        title: "Shop Under Review",
        text: "Your shop has been submitted and is currently under review by our admin team. You'll be notified once it's approved.",
        confirmLabel: null,
        showCancel: true,
        closeable: true,
      };
    }
    if (shopStatus === "Rejected") {
      return {
        emoji: "🚫",
        title: "Shop Rejected",
        text: "Unfortunately, your shop has been rejected. You cannot add products, receive orders, or make topup requests until your shop is approved. Please contact support or resubmit your shop with correct details.",
        confirmLabel: "Resubmit Shop",
        onConfirm: handleCreateStore,
        showCancel: false,
        closeable: false,
      };
    }
    return null;
  };

  const modalContent = getModalContent();

  const cards = [
    {
      label: "Total Products",
      value: dashboardData?.total_products?.toLocaleString() ?? "0",
      icon: <FiLayers />,
      iconClass: "dv-overview-icon",
    },
    {
      label: "Orders",
      value: dashboardData?.total_orders?.toLocaleString() ?? "0",
      icon: <FiClipboard />,
      iconClass: "dv-overview-icon",
    },
    {
      label: "Total Stock",
      value: dashboardData?.total_stock?.toLocaleString() ?? "0",
      icon: <FiBox />,
      iconClass: "dv-overview-icon",
    },
    {
      label: "Out of Stock",
      value: dashboardData?.out_of_stock?.toLocaleString() ?? "0",
      icon: <FiAlertCircle />,
      iconClass: "dv-overview-icon dv-overview-icon--alert",
    },
  ];

  const expenseProfitData = dashboardData?.expense_profit_data ?? [];
  const hasExpenseProfitData = expenseProfitData.length > 0;

  const FALLBACK_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
  const epLabels = hasExpenseProfitData
    ? expenseProfitData.map((d) => d.month)
    : FALLBACK_MONTHS;
  const epExpense = hasExpenseProfitData
    ? expenseProfitData.map((d) => d.expense)
    : FALLBACK_MONTHS.map(() => 0);
  const epProfit = hasExpenseProfitData
    ? expenseProfitData.map((d) => d.profit)
    : FALLBACK_MONTHS.map(() => 0);

  return (
    <SellerMasterLayout>
      <div className="dv-wrapper">
        <div className="dv-shell">
          <div className="dv-overview">
            {cards.map((card, idx) => (
              <div key={idx} className="dv-overview-card">
                <div>
                  <span className="dv-overview-label">{card.label}</span>
                  <h3 className="dv-overview-value">{card.value}</h3>
                </div>
                <div className={card.iconClass}>{card.icon}</div>
              </div>
            ))}
          </div>

          <div className="dv-mid-grid">
            <div className="dv-card">
              <h4 className="dv-card-title">Total Customers</h4>
              <p className="dv-big-number">
                {dashboardData?.total_customers?.toLocaleString() || "0"}
              </p>
            </div>

            <div className="dv-card">
              <h4 className="dv-card-title">Inventory Values</h4>
              <div
                className="dv-donut"
                style={{
                  background: `conic-gradient(#fb7f4f 0deg ${
                    (dashboardData?.inventory_percentage ?? 0) * 3.6
                  }deg, #ffe0d3 ${
                    (dashboardData?.inventory_percentage ?? 0) * 3.6
                  }deg 360deg)`,
                }}
              >
                <div className="dv-donut-inner">
                  {dashboardData?.inventory_percentage ?? 0}%
                </div>
              </div>

              <div className="dv-legend">
                <span>
                  <span className="dv-dot dv-dot--accent"></span> Sold units
                </span>
                <span>
                  <span className="dv-dot dv-dot--light"></span> Total units
                </span>
              </div>
            </div>
          </div>

          <div className="dv-card">
            <h4 className="dv-card-title">Expense vs Profit</h4>

            <StackedBarChart
              labels={epLabels}
              datasets={[
                { label: "Expense", data: epExpense, color: "#378ADD" },
                { label: "Profit", data: epProfit, color: "#eb6834" },
              ]}
            />
          </div>
        </div>
      </div>

      {showModal && modalContent && (
        <div
          className="dv-modal-overlay"
          onClick={() => modalContent.closeable && setShowModal(false)}
        >
          <div className="dv-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dv-modal-icon-wrap">
              <div className="dv-modal-ring dv-modal-ring--1" />
              <div className="dv-modal-ring dv-modal-ring--2" />
              <div className="dv-modal-icon-circle">
                <span className="dv-modal-emoji">{modalContent.emoji}</span>
              </div>
            </div>

            {modalContent.closeable && (
              <button
                className="dv-modal-close"
                onClick={() => setShowModal(false)}
              >
                <FiX size={15} />
              </button>
            )}

            <h2 className="dv-modal-title">{modalContent.title}</h2>
            <p className="dv-modal-text">{modalContent.text}</p>

            {modalContent.confirmLabel && (
              <button
                className="dv-modal-btn-confirm"
                onClick={modalContent.onConfirm}
              >
                {modalContent.confirmLabel} &nbsp;
                <FiArrowRight size={14} />
              </button>
            )}

            {modalContent.showCancel && (
              <button
                className="dv-modal-btn-cancel"
                onClick={() => setShowModal(false)}
              >
                I'll do it later
              </button>
            )}
          </div>
        </div>
      )}
    </SellerMasterLayout>
  );
};

export default DashboardView;